// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber
} from 'firebase/auth';
import { auth } from '../../firebase/config';
import { apiService } from '../../services/api';
import { adminApi } from '../../services/adminApi';
const initialState = {isHydrated: false,
  authChecked: false,
  user: null,
  profile: null,isAdmin: false,
  isAuthenticated: false,
  loading: false,
  error: null,
  confirmationResult: null,
  needsProfileSetup: false,
};

// Helper function to fetch or create profile
const fetchOrCreateProfile = async (firebaseUser) => {
  try {
    const profile = await apiService.getMyProfile();
    return { profile, needsProfileSetup: false };
  } catch (error) {
    console.log(error)
    if (error.response && error.response.status === 404) {
      return { profile: null, needsProfileSetup: true };
    }
    throw error;
  }
};
// Admin status rides along on the profile (`is_admin` from GET /api/creators/me),
// so the common path costs no extra request. The probe below is only a fallback
// for backends that predate that field — it can go once every env is deployed.
// Either way this is a display/routing hint; admin routes enforce access server-side.
export const resolveIsAdmin = async (profile) => {
  if (!profile) return false;
  if (typeof profile.is_admin === 'boolean') return profile.is_admin;

  try {
    await adminApi.getMyAccess();
    return true;
  } catch {
    return false;
  }
};
// ============================================
// ASYNC THUNKS
// ============================================

// Sign Up with Email
export const signUpWithEmail = createAsyncThunk(
  'auth/signUpWithEmail',
  async ({ email, password, name, creatorType }, { rejectWithValue }) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const profile = await apiService.createCreator({
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email,
        name,
        creator_type: creatorType,
      });

      return {
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name,
          displayName: name,
          loginProvider: 'email',
        },
        profile,
        needsProfileSetup: false,
        isAdmin: false, // freshly created account is never an admin
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Sign In with Email
export const signInWithEmail = createAsyncThunk(
  'auth/signInWithEmail',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      const { profile, needsProfileSetup } = await fetchOrCreateProfile(firebaseUser);
      const isAdmin = await resolveIsAdmin(profile);
      return {
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name:profile?.name,
          displayName: firebaseUser.displayName,
          loginProvider: 'email',
        },
        profile,
        needsProfileSetup,isAdmin
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Sign In with Google
export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      const { profile, needsProfileSetup } = await fetchOrCreateProfile(firebaseUser);
      const isAdmin = await resolveIsAdmin(profile);
      return {
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: profile?.name,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          loginProvider: 'google',
        },
        profile,
        needsProfileSetup,isAdmin
      };
    } catch (error) {
      console.log(error)
      return rejectWithValue(error.message);
    }
  }
);

// Sign In with Apple
export const signInWithApple = createAsyncThunk(
  'auth/signInWithApple',
  async (_, { rejectWithValue }) => {
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      const { profile, needsProfileSetup } = await fetchOrCreateProfile(firebaseUser);
      const isAdmin = await resolveIsAdmin(profile);
      return {
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: profile?.name,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          loginProvider: 'apple',
        },
        profile,
        needsProfileSetup,isAdmin
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Send OTP to Phone
export const sendPhoneOTP = createAsyncThunk(
  'auth/sendPhoneOTP',
  async ({ phoneNumber, recaptchaVerifier }, { rejectWithValue }) => {
    try {
      const fullPhoneNumber = `+91${phoneNumber}`;
      const confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, recaptchaVerifier);
      return confirmation;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Verify Phone OTP
export const verifyPhoneOTP = createAsyncThunk(
  'auth/verifyPhoneOTP',
  async ({ confirmationResult, otp }, { rejectWithValue }) => {
    try {
      const result = await confirmationResult.confirm(otp);
      const firebaseUser = result.user;

      const { profile, needsProfileSetup } = await fetchOrCreateProfile(firebaseUser);
      const isAdmin = await resolveIsAdmin(profile);
      return {
        user: {
          uid: firebaseUser.uid,
          phoneNumber: firebaseUser.phoneNumber,
          // Present when this number was linked to an account first created
          // with Google or email; absent for a mobile-only signup.
          email: firebaseUser.email || profile?.email || null,
          name: profile?.name,
          displayName: firebaseUser.displayName,
          loginProvider: 'phone',
        },
        profile,
        needsProfileSetup,isAdmin
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Complete Profile Setup
export const completeProfileSetup = createAsyncThunk(
  'auth/completeProfileSetup',
  async ({ name, email, creatorType, acceptedTerms, confirmedAdult }, { getState, rejectWithValue }) => {
    try {
      const { auth: authState } = getState();
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error('No authenticated user');
      }

      let referralPayload = {};
      try {
        const ref = JSON.parse(localStorage.getItem('ml_referral') || 'null');
        if (ref?.code) {
          referralPayload = { referred_by_code: ref.code, referred_source: ref.source || 'direct' };
        }
      } catch {}

      const profile = await apiService.createCreator({
        firebase_uid: firebaseUser.uid,
        email: firebaseUser?.email || authState?.user?.email || email,
        name,
        creator_type: creatorType,
        phone: firebaseUser.phoneNumber,
        accepted_terms: !!acceptedTerms,
        accepted_terms_at: acceptedTerms ? new Date().toISOString() : null,
        confirmed_adult: !!confirmedAdult,
        confirmed_adult_at: confirmedAdult ? new Date().toISOString() : null,
        signup_method: authState?.user?.loginProvider || 'unknown',
        ...referralPayload,
      });

      // Clear referral after successful signup so it isn't reused
      try { localStorage.removeItem('ml_referral'); } catch {}
      return {
        profile,
        user: {
          ...authState.user,
          name: name,
        },
        isAdmin: false, // freshly created account is never an admin
      };
    } catch (error) {
      // Surface the backend message (e.g. registrations closed) instead of a generic axios string.
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// Sign Out
export const signOutUser = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      await firebaseSignOut(auth);
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Profile
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      // No admin probe here: fetchProfile.fulfilled never reads isAdmin, and the
      // auth listener already resolves it once per session.
      return await apiService.getMyProfile();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const checkAdmin = createAsyncThunk(
  'auth/checkAdmin',
  async () => {
    try {
      await adminApi.getMyAccess();
      return true;
    } catch {
      return false;
    }
  }
);
// ============================================
// SLICE
// ============================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Auth hydration complete
    authHydrated: (state) => {
      state.authChecked = true;state.isHydrated = true;
    },

    // Set user (used by auth listener)
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
//  authHydrated: (state) => {
//       s // ADD THIS
//     },
    // Set profile (used by auth listener)
setProfile: (state, action) => {
      state.isAuthenticated = true;
      state.profile = action.payload;
      state.user = { ...state.user, ...action.payload };
      state.authChecked = true;
      state.isAdmin = action.payload?.isAdmin || false;  // 👈 ADD THIS
    },setAdminStatus: (state, action) => {
      state.isAdmin = action.payload;
    },

    // Require profile setup
    requireProfileSetup: (state) => {
      state.isAuthenticated = true;
      state.needsProfileSetup = true;state.isAdmin = false;
    },

    // Set confirmation result for phone auth
    setConfirmationResult: (state, action) => {
      state.confirmationResult = action.payload;
    },

    // Mark physical product seller terms as accepted (optimistic local update)
    setPhysicalTermsAccepted: (state) => {
      if (state.profile) {
        state.profile.physical_terms_accepted = true;
      }
    },

    // Mark Review My Work Creator Agreement as accepted (optimistic local update)
    setReviewServiceTermsAccepted: (state) => {
      if (state.profile) {
        state.profile.review_service_terms_accepted = true;
      }
    },

    // Mark the Services (freelance marketplace) Seller Agreement as accepted.
    // Separate flag from the Review My Work one above — different fee split and
    // a GST declaration, so accepting one must never imply the other.
    setGigSellerTermsAccepted: (state) => {
      if (state.profile) {
        state.profile.gig_seller_terms_accepted = true;
      }
    },

    // Mark the MintLink Forms Creator Agreement as accepted — its own flag,
    // enforced server-side at publish time (unlike the two above).
    setPdfFormsTermsAccepted: (state) => {
      if (state.profile) {
        state.profile.pdf_forms_terms_accepted = true;
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Logout (synchronous)
    logout: (state) => {
      state.user = null;
      state.profile = null;
      state.isAuthenticated = false;
      state.error = null;
      state.confirmationResult = null;
      state.needsProfileSetup = false;
      state.authChecked = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // ============================================
      // Sign Up with Email
      // ============================================
      .addCase(signUpWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpWithEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.isAuthenticated = true;
        state.needsProfileSetup = false;
      })
      .addCase(signUpWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }).addCase(checkAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAdmin = action.payload;
      })
      .addCase(checkAdmin.rejected, (state) => {
        state.loading = false;
        state.isAdmin = false;
      })

      // ============================================
      // Sign In with Email
      // ============================================
      .addCase(signInWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.isAuthenticated = true;
        state.needsProfileSetup = action.payload.needsProfileSetup;state.isAdmin = action.payload.isAdmin; 
      })
      .addCase(signInWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Sign In with Google
      // ============================================
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.isAuthenticated = true;
        state.needsProfileSetup = action.payload.needsProfileSetup;state.isAdmin = action.payload.isAdmin; 
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Sign In with Apple
      // ============================================
      .addCase(signInWithApple.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithApple.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.isAuthenticated = true;
        state.needsProfileSetup = action.payload.needsProfileSetup;state.isAdmin = action.payload.isAdmin; 
      })
      .addCase(signInWithApple.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Send Phone OTP
      // ============================================
      .addCase(sendPhoneOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendPhoneOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.confirmationResult = action.payload;state.isAdmin = action.payload.isAdmin; 
      })
      .addCase(sendPhoneOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Verify Phone OTP
      // ============================================
      .addCase(verifyPhoneOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPhoneOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.isAuthenticated = true;
        state.confirmationResult = null;
        state.needsProfileSetup = action.payload.needsProfileSetup;state.isAdmin = action.payload.isAdmin; 
      })
      .addCase(verifyPhoneOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Complete Profile Setup
      // ============================================
      .addCase(completeProfileSetup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeProfileSetup.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.profile;
        state.user = action.payload.user;
        // Don't set needsProfileSetup = false here - let onboarding complete naturally
        state.isAdmin = action.payload.isAdmin;
      })
      .addCase(completeProfileSetup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Sign Out
      // ============================================
      .addCase(signOutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.confirmationResult = null;
        state.needsProfileSetup = false;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // Fetch Profile
      // ============================================
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.needsProfileSetup = false;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// ============================================
// EXPORTS
// ============================================

export const {
  authHydrated,
  setUser,
  setProfile, setAdminStatus,
  setPhysicalTermsAccepted,
  setReviewServiceTermsAccepted,
  setGigSellerTermsAccepted,
  setPdfFormsTermsAccepted,
  requireProfileSetup,
  setConfirmationResult,
  clearError,
  logout,
} = authSlice.actions;

export default authSlice.reducer;